import { createPortal } from 'react-dom';
import { useEffect, useRef, useState } from 'react';

import { CloseButton } from '../close-button';

interface IImageZoomModalProps {
  src: string;
  onClose: () => void;
}

export const ImageZoomModal = ({ src, onClose }: IImageZoomModalProps) => {
  const [visible, setVisible] = useState(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const closeTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // Fade out first, then unmount.
  const close = () => {
    setVisible(false);
    clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => onCloseRef.current(), 200);
  };

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 10);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKeyDown);

    return () => {
      clearTimeout(timer);
      clearTimeout(closeTimerRef.current);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  return createPortal(
    <div
      className={`fixed inset-0 z-modal flex items-center justify-center bg-black/70 transition-opacity duration-300 ${
        visible ? 'opacity-100' : 'opacity-0'
      }`}
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
        e.stopPropagation();
      }}
    >
      <div className="relative">
        <CloseButton
          tone="solid"
          className="absolute top-4 right-4 z-50"
          onClick={(e) => {
            e.stopPropagation();
            close();
          }}
        />
        <img
          src={src}
          alt=""
          className={`w-auto h-auto max-w-[90vw] max-h-[90vh] transform transition-transform duration-300 rounded-card ${
            visible ? 'scale-100' : 'scale-95'
          }`}
        />
      </div>
    </div>,
    document.body,
  );
};
